import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3,
  CheckCircle,
  ArrowRight,
  Loader2,
  AlertCircle,
  Globe,
  ExternalLink,
  Trash2,
  Shield
} from 'lucide-react';
import { analyticsService, AnalyticsConnection } from '@/services/analytics';

const GoogleAnalyticsSettings: React.FC = () => {
  const [activeConnection, setActiveConnection] = useState<AnalyticsConnection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const connection = await analyticsService.getActiveConnection();
      setActiveConnection(connection);
    } catch (err: any) {
      console.error('Error loading analytics connection:', err);
      setError(err.message || 'Failed to load connection status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectGoogleAnalytics = () => {
    try {
      setIsConnecting(true);
      setError(null);
      
      // Start OAuth flow via analytics service
      // This will redirect to Google OAuth in the same window
      analyticsService.startOAuthFlow();
    } catch (err: any) {
      setError(err.message || 'Failed to connect Google Analytics');
      console.error('OAuth error:', err);
      setIsConnecting(false);
    }
  };


  const handleDisconnect = async () => {
    if (!activeConnection) return;
    
    try {
      await analyticsService.deleteConnection(activeConnection.id);
      setActiveConnection(null);
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect Google Analytics');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading analytics connection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Google Analytics Connection</span>
          </CardTitle>
          <CardDescription>
            Connect your Google Analytics account to view website performance data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {activeConnection ? (
            <div className="space-y-4">
              {/* Connected Status */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <p className="text-sm font-medium text-green-800">
                    Connected to {activeConnection.name || 'Google Analytics'}
                  </p>
                </div>
                {activeConnection.website_url && (
                  <p className="text-sm text-green-600 mt-1">
                    Website: {activeConnection.website_url}
                  </p>
                )}
                <div className="flex items-center space-x-2 mt-2">
                  <Badge variant="secondary" className="text-xs">
                    {activeConnection.is_active || 'Active'}
                  </Badge>
                  <span className="text-xs text-green-600">
                    Connected on {new Date(activeConnection.created_at || Date.now()).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {/* <Button 
                  onClick={handleManageConnection}
                  variant="outline"
                  size="sm"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Manage Connection
                </Button> */}
                <Button 
                  onClick={handleConnectGoogleAnalytics}
                  size="sm"
                >
                  <BarChart3 className="mr-2 h-4 w-4" />
                  Reconnect
                </Button>
                <Button 
                  onClick={handleDisconnect}
                  variant="destructive"
                  size="sm"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Not Connected */}
              <div className="bg-muted/30 border border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">No Google Analytics Connected</h3>
                <p className="text-muted-foreground mb-4">
                  Connect your Google Analytics account to view website performance data, 
                  traffic insights, and user behavior analytics.
                </p>
                
                <Button 
                  onClick={handleConnectGoogleAnalytics}
                  disabled={isConnecting}
                  size="lg"
                  className="px-8"
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="mr-2 h-5 w-5" />
                      Connect Google Analytics
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Benefits Card */}
      <Card>
        <CardHeader>
          <CardTitle>Benefits of Connecting Google Analytics</CardTitle>
          <CardDescription>
            What you'll get when you connect your Google Analytics account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-start space-x-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Globe className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h4 className="font-medium">Website Performance</h4>
                <p className="text-sm text-muted-foreground">
                  Track page views, bounce rates, and user engagement metrics
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <BarChart3 className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h4 className="font-medium">Traffic Insights</h4>
                <p className="text-sm text-muted-foreground">
                  Analyze traffic sources, demographics, and user behavior
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <ExternalLink className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h4 className="font-medium">SEO Integration</h4>
                <p className="text-sm text-muted-foreground">
                  Correlate keyword performance with website analytics data
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h4 className="font-medium">Secure Connection</h4>
                <p className="text-sm text-muted-foreground">
                  Read-only access with OAuth 2.0 security standards
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Info */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <h4 className="font-medium mb-1">Security & Privacy</h4>
              <p className="text-sm text-muted-foreground">
                We use OAuth 2.0 for secure authentication. We only request read-only access to your 
                Google Analytics data and never store your Google account credentials. You can disconnect 
                at any time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleAnalyticsSettings;
